import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { NAV_BY_ROLE, SETTINGS_ITEM, type AppRole, type NavGroup, type NavItem } from "@/lib/nav";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import SignUpPromptDialog from "@/components/auth/SignUpPromptDialog";

interface AppSidebarProps {
  role: AppRole;
}

function isActive(pathname: string, to: string, exact?: boolean) {
  if (exact) return pathname === to;
  return pathname === to || pathname.startsWith(to + "/");
}

export default function AppSidebar({ role }: AppSidebarProps) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const groups: NavGroup[] = NAV_BY_ROLE[role] ?? [];
  const [gate, setGate] = useState<{ open: boolean; item: NavItem | null }>({
    open: false,
    item: null,
  });

  const handleClick = (e: React.MouseEvent, item: NavItem) => {
    if (!user && item.requiresAuth) {
      e.preventDefault();
      setGate({ open: true, item });
    }
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b">
        <div className={`flex items-center py-2 ${collapsed ? "justify-center px-0" : "px-2"}`}>
          {collapsed ? (
            <img
              src="https://n-micro.ca/images/gradetestpro.png"
              alt="Grade Test Pro"
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <Logo horizontal />
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {groups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const active = isActive(location.pathname, item.to, item.exact);
                  return (
                    <SidebarMenuItem key={`${item.to}-${item.label}`}>
                      <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
                        <NavLink
                          to={item.to}
                          end={item.exact}
                          onClick={(e) => handleClick(e, item)}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}

        <SidebarGroup>
          <SidebarGroupLabel>Account</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {user ? (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(location.pathname, SETTINGS_ITEM.to, true)}
                    tooltip={SETTINGS_ITEM.label}
                  >
                    <NavLink to={SETTINGS_ITEM.to} end>
                      <SETTINGS_ITEM.icon className="h-4 w-4" />
                      <span>{SETTINGS_ITEM.label}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Sign up">
                    <button
                      type="button"
                      onClick={() => navigate("/auth?mode=signup")}
                      className="w-full"
                    >
                      <SETTINGS_ITEM.icon className="h-4 w-4" />
                      <span>Sign up / sign in</span>
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SignUpPromptDialog
        open={gate.open}
        onOpenChange={(open) => setGate((g) => ({ ...g, open }))}
        feature={gate.item?.label ?? "this feature"}
        redirectTo={gate.item?.to}
      />
    </Sidebar>
  );
}
